import { useState } from "react";
import { CommerceDialog } from "../commerce/CommerceDialog";
import { Btn } from "../ui/Btn";
import { Inp } from "../ui/Inp";
import { C } from "../../constants/theme";
import { toast } from "../../hooks/useToast";
import { buscarFotosMakerWorld, importarFotosMakerWorld } from "./makerworldApi";
import { GradeFotosMakerWorld } from "./GradeFotosMakerWorld";
import { BotaoColarLink } from "./BotaoColarLink";
import { useColarLink } from "../../hooks/useColarLink";

// Botão + modal "Importar fotos do MakerWorld".
//   1) cola o link -> backend busca o modelo e lista as fotos;
//   2) grade com checkbox (todas marcadas) -> "Importar selecionadas";
//   3) backend baixa, valida e guarda no Storage do produto; aqui só devolvemos
//      as fotos já salvas ({ path, url }) pra quem chamou registrar no produto.
// `onImportadas(novas)` deve gravar as fotos no produto (mesmo caminho das
// fotos enviadas à mão) — este componente não conhece o Firestore.
// `fotosAtuais`: galeria do produto — foto do MakerWorld que já está nela vem
// desmarcada (o id vai no nome do arquivo: …_makerworld_<id>.jpg).
export function ImportarFotosMakerWorld({ produtoId, onImportadas, disabled, linkInicial = "", fotosAtuais = [] }) {
  const [aberto, setAberto] = useState(false);
  const [link, setLink] = useState(linkInicial);
  const [buscando, setBuscando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [erro, setErro] = useState("");
  const [modelo, setModelo] = useState(null);
  const [marcadas, setMarcadas] = useState(() => new Set());

  // Ctrl+V vale só enquanto o modal está aberto e ainda na etapa do link.
  const colar = useColarLink(
    (valor) => { setLink(valor); setErro(""); },
    { ativo: aberto && !modelo && !buscando },
  );

  const fechar = () => {
    if (importando) return;
    setAberto(false); setLink(linkInicial); setErro(""); setModelo(null); setMarcadas(new Set());
  };
  const jaNaGaleria = new Set(fotosAtuais.map(f => /_makerworld_([^./]+)\./.exec(f?.path || "")?.[1]).filter(Boolean));

  const buscar = async (alvo = link) => {
    if (!alvo.trim() || buscando) return;
    setBuscando(true); setErro("");
    try {
      const r = await buscarFotosMakerWorld(alvo.trim());
      setModelo(r);
      setMarcadas(new Set(r.images.filter(i => !jaNaGaleria.has(i.id)).map(i => i.id)));
    } catch (e) { setErro(e.message); }
    setBuscando(false);
  };

  // Abre já com o link salvo no produto (se houver), mesmo que ele tenha sido
  // preenchido/alterado depois do primeiro render — e aí vai direto pra escolha das fotos.
  const abrir = () => {
    const alvo = link.trim() ? link : linkInicial;
    setLink(alvo); setAberto(true);
    if (/^https?:\/\/\S+$/i.test(alvo.trim())) buscar(alvo);
  };

  const alternar = (id) => setMarcadas(atual => {
    const novo = new Set(atual);
    novo.has(id) ? novo.delete(id) : novo.add(id);
    return novo;
  });
  const todasMarcadas = modelo && marcadas.size === modelo.images.length;

  const importar = async () => {
    if (!modelo || marcadas.size === 0 || importando) return;
    setImportando(true); setErro("");
    try {
      // Mantém a ordem do MakerWorld (capa primeiro), não a ordem dos cliques.
      const ids = modelo.images.filter(i => marcadas.has(i.id)).map(i => i.id);
      const r = await importarFotosMakerWorld({ url: modelo.sourceUrl, ids, produtoId });
      if (r.importadas.length > 0) await onImportadas(r.importadas.map(({ path, url }) => ({ path, url })), { url: modelo.sourceUrl });
      if (r.falhas.length === 0) {
        toast(r.importadas.length > 1 ? `${r.importadas.length} fotos importadas do MakerWorld ✓` : "Foto importada do MakerWorld ✓", "success");
        setImportando(false); setAberto(false); setLink(""); setModelo(null); setMarcadas(new Set());
        return;
      }
      if (r.importadas.length > 0) {
        toast(`${r.importadas.length} importada(s); ${r.falhas.length} não deu(ram) certo — tente de novo só com ela(s).`, "warning");
        // Mantém aberto só com as que falharam, prontas pra nova tentativa.
        const falharam = new Set(r.falhas.map(f => f.id));
        setModelo(m => ({ ...m, images: m.images.filter(i => falharam.has(i.id)) }));
        setMarcadas(falharam);
      } else {
        setErro("Nenhuma foto pôde ser importada. Tente novamente em instantes.");
      }
    } catch (e) { setErro(e.message); }
    setImportando(false);
  };

  return (
    <>
      <button type="button" onClick={abrir} disabled={disabled}
        style={{ background:"none", border:"none", color:C.muted, fontSize:11, cursor:disabled ? "default" : "pointer", padding:"2px 0", textDecoration:"underline" }}>
        🧩 Importar fotos do MakerWorld
      </button>

      {aberto && (
        <CommerceDialog
          title={modelo ? "Fotos encontradas no MakerWorld" : "Importar fotos do MakerWorld"}
          subtitle={modelo ? modelo.title : "Cole o link do modelo e escolha quais fotos usar."}
          onClose={fechar}
          footer={modelo ? (
            <>
              <Btn outline small color={C.muted} onClick={() => setMarcadas(todasMarcadas ? new Set() : new Set(modelo.images.map(i => i.id)))} disabled={importando} style={{ marginRight:"auto" }}>
                {todasMarcadas ? "Limpar seleção" : "Selecionar todas"}
              </Btn>
              <Btn outline color={C.muted} onClick={fechar} disabled={importando}>Cancelar</Btn>
              <Btn color={C.accent2} onClick={importar} disabled={importando || marcadas.size === 0}>
                {importando ? "⏳ Importando…" : `Importar selecionadas (${marcadas.size})`}
              </Btn>
            </>
          ) : (
            <>
              <Btn outline color={C.muted} onClick={fechar}>Cancelar</Btn>
              <Btn color={C.accent2} onClick={() => buscar()} disabled={buscando || !link.trim()}>{buscando ? "⏳ Buscando…" : "Buscar fotos"}</Btn>
            </>
          )}>
          {!modelo ? (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <Inp label="LINK DO MODELO" value={link} placeholder="https://makerworld.com/pt/models/…"
                    onChange={e => { setLink(e.target.value); setErro(""); }}
                    onKeyDown={e => { if (e.key === "Enter") buscar(); }} />
                </div>
                <BotaoColarLink colar={colar} desabilitado={buscando} style={{ minHeight:38 }} />
              </div>
              <p className="commerce-hint" style={{ margin:0 }}>Copiou o link? Ctrl+V aqui já cola. As fotos serão copiadas para o seu produto — elas continuam funcionando mesmo que o MakerWorld mude ou remova o modelo.</p>
            </div>
          ) : (
            <GradeFotosMakerWorld imagens={modelo.images} marcadas={marcadas} onAlternar={alternar} desabilitado={importando} jaAdicionadas={jaNaGaleria} />
          )}
          {erro && <p role="alert" style={{ margin:"12px 0 0", color:C.danger, fontSize:13 }}>{erro}</p>}
        </CommerceDialog>
      )}
    </>
  );
}
